import React, { useEffect, useMemo, useRef, useState } from "react";
import { Modal, Form, Select, DatePicker, InputNumber, Input, Alert, Spin } from "antd";
import dayjs from "dayjs";
import {
  createCita,
  crearSeguimiento,
  searchPacientes,
  getPaciente,
  listProcedimientosQuirurgicos,
} from "../../api/agendaApi";
import { TIPO_CITA_OPTIONS, esMenorDeEdad } from "./agendaHelpers";
import useDoctorOptions from "./useDoctorOptions";
import { toast } from "../toasts/toastStore";

const { TextArea } = Input;

// Modal de alta de cita. Se reutiliza tambien para "seguimiento" (crea una
// cita nueva ligada a una de origen via POST /citas/{id}/seguimiento) cuando
// se le pasa `seguimientoDeIdCita` + `valoresIniciales`.
export default function CitaFormModal({ open, onClose, onCreated, seguimientoDeIdCita, valoresIniciales }) {
  const [form] = Form.useForm();
  const [guardando, setGuardando] = useState(false);
  const [pacientes, setPacientes] = useState([]);
  const [buscandoPaciente, setBuscandoPaciente] = useState(false);
  const [pacienteSel, setPacienteSel] = useState(null);
  const [procedimientos, setProcedimientos] = useState([]);
  const busquedaTimer = useRef(null);
  const tipoCita = Form.useWatch("tipo_cita", form);

  const esSeguimiento = Boolean(seguimientoDeIdCita);
  const { doctores, disponible: doctoresDisponibles } = useDoctorOptions();

  useEffect(() => {
    if (!open) return;
    listProcedimientosQuirurgicos().then(setProcedimientos).catch(() => setProcedimientos([]));
    form.resetFields();
    setPacienteSel(null);
    setPacientes([]);
    // Lista inicial: sin escribir nada ya se ven pacientes para elegir.
    setBuscandoPaciente(true);
    searchPacientes("")
      .then(async (items) => {
        const idInicial = valoresIniciales?.id_paciente;
        if (idInicial && !items.some((x) => x.id_paciente === idInicial)) {
          try {
            const inicial = await getPaciente(idInicial);
            if (inicial?.id_paciente) items = [inicial, ...items];
          } catch {
            /* el select mostrara solo el id si no se puede cargar */
          }
        }
        setPacientes(items);
        if (idInicial) setPacienteSel(items.find((x) => x.id_paciente === idInicial) || null);
      })
      .catch(() => setPacientes([]))
      .finally(() => setBuscandoPaciente(false));
    if (valoresIniciales) {
      form.setFieldsValue(valoresIniciales);
    } else {
      form.setFieldsValue({ tipo_cita: esSeguimiento ? "seguimiento" : "consulta_general", origen: "interno", duracion_minutos: 30 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const buscarPaciente = (valor) => {
    clearTimeout(busquedaTimer.current);
    busquedaTimer.current = setTimeout(() => {
      setBuscandoPaciente(true);
      searchPacientes((valor || "").trim())
        .then((items) => setPacientes(items))
        .catch(() => setPacientes([]))
        .finally(() => setBuscandoPaciente(false));
    }, 300);
  };

  useEffect(() => () => clearTimeout(busquedaTimer.current), []);

  const menor = useMemo(() => esMenorDeEdad(pacienteSel?.fecha_nacimiento), [pacienteSel]);

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      setGuardando(true);

      const payload = {
        id_paciente: values.id_paciente,
        id_user_doctor: values.id_user_doctor || null,
        tipo_cita: values.tipo_cita,
        origen: values.origen || "interno",
        fecha_hora_inicio: values.fecha_hora_inicio.format("YYYY-MM-DD HH:mm:ss"),
        duracion_minutos: values.duracion_minutos,
        motivo: values.motivo || null,
      };

      if (!esSeguimiento) {
        if (menor) {
          payload.autorizacion_menor = {
            nombre_autoriza: values.nombre_autoriza,
            parentesco: values.parentesco,
            telefono: values.telefono_autoriza || null,
          };
        }
        if (values.tipo_cita === "cirugia") {
          payload.cirugia = {
            id_procedimiento: values.id_procedimiento,
            notas: values.notas_cirugia || null,
          };
        }
      }

      const resultado = esSeguimiento
        ? await crearSeguimiento(seguimientoDeIdCita, payload)
        : await createCita(payload);

      toast.success(esSeguimiento ? "Cita de seguimiento creada" : "Cita creada correctamente");
      onCreated && onCreated(resultado.item || resultado);
    } catch (err) {
      if (err?.errorFields) return; // error de validacion de antd, no de red
      toast.error(err?.response?.data?.detail || "No se pudo guardar la cita");
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Modal
      title={esSeguimiento ? "Nueva cita de seguimiento" : "Nueva cita"}
      open={open}
      onCancel={onClose}
      onOk={handleOk}
      confirmLoading={guardando}
      okText="Guardar"
      cancelText="Cancelar"
      destroyOnClose
      width={640}
    >
      <Form form={form} layout="vertical" requiredMark={false}>
        <Form.Item
          name="id_paciente"
          label="Paciente"
          rules={[{ required: true, message: "Selecciona un paciente" }]}
        >
          <Select
            showSearch
            loading={buscandoPaciente}
            placeholder="Buscar paciente por nombre o teléfono"
            filterOption={false}
            notFoundContent={buscandoPaciente ? <Spin size="small" /> : "Sin resultados"}
            onSearch={buscarPaciente}
            onChange={(val) => {
              const p = pacientes.find((x) => x.id_paciente === val);
              setPacienteSel(p || null);
            }}
            options={pacientes.map((p) => ({
              value: p.id_paciente,
              label: `${p.nombre_completo}${p.telefono ? " · " + p.telefono : ""}`,
            }))}
          />
        </Form.Item>

        {menor && !esSeguimiento && (
          <Alert
            type="warning"
            showIcon
            message="Paciente menor de edad"
            description="Se requiere autorización de un adulto responsable para esta cita."
            style={{ marginBottom: 16 }}
          />
        )}

        <Form.Item
          name="tipo_cita"
          label="Tipo de cita"
          rules={[{ required: true }]}
        >
          <Select options={TIPO_CITA_OPTIONS} disabled={esSeguimiento} />
        </Form.Item>

        <Form.Item name="origen" label="Origen" initialValue="interno">
          <Select
            options={[
              { value: "interno", label: "Interno" },
              { value: "doctoralia", label: "Doctoralia" },
            ]}
          />
        </Form.Item>

        <div style={{ display: "flex", gap: 16 }}>
          <Form.Item
            name="fecha_hora_inicio"
            label="Fecha y hora"
            rules={[{ required: true, message: "Selecciona fecha y hora" }]}
            style={{ flex: 1 }}
          >
            <DatePicker showTime format="YYYY-MM-DD HH:mm" style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item
            name="duracion_minutos"
            label="Duración (min)"
            rules={[{ required: true, message: "Duración requerida" }]}
            style={{ width: 160 }}
          >
            <InputNumber min={5} step={5} style={{ width: "100%" }} />
          </Form.Item>
        </div>

        <div style={{ display: "flex", gap: 16 }}>
          <Form.Item name="id_user_doctor" label="Doctor" style={{ flex: 1 }}>
            {doctoresDisponibles ? (
              <Select
                allowClear
                showSearch
                placeholder="Selecciona doctor"
                optionFilterProp="label"
                options={doctores.map((d) => ({ value: d.id_user, label: d.nombre }))}
              />
            ) : (
              <InputNumber
                style={{ width: "100%" }}
                min={1}
                placeholder="ID de usuario doctor"
              />
            )}
          </Form.Item>
        </div>

        {tipoCita === "cirugia" && !esSeguimiento && (
          <>
            <Form.Item
              name="id_procedimiento"
              label="Procedimiento quirúrgico"
              rules={[{ required: true, message: "Selecciona el procedimiento" }]}
            >
              <Select
                placeholder="Selecciona procedimiento"
                options={procedimientos.map((p) => ({ value: p.id_procedimiento, label: p.nombre }))}
              />
            </Form.Item>
            <Form.Item name="notas_cirugia" label="Notas de cirugía">
              <TextArea rows={2} />
            </Form.Item>
          </>
        )}

        {menor && !esSeguimiento && (
          <>
            <Form.Item
              name="nombre_autoriza"
              label="Nombre de quien autoriza"
              rules={[{ required: true, message: "Requerido para menores de edad" }]}
            >
              <Input />
            </Form.Item>
            <div style={{ display: "flex", gap: 16 }}>
              <Form.Item
                name="parentesco"
                label="Parentesco"
                rules={[{ required: true, message: "Requerido" }]}
                style={{ flex: 1 }}
              >
                <Input placeholder="Madre, padre, tutor..." />
              </Form.Item>
              <Form.Item name="telefono_autoriza" label="Teléfono de contacto" style={{ flex: 1 }}>
                <Input />
              </Form.Item>
            </div>
          </>
        )}

        <Form.Item name="motivo" label="Motivo">
          <TextArea rows={3} placeholder="Motivo de la cita" />
        </Form.Item>
      </Form>
    </Modal>
  );
}

